import React, { forwardRef, useEffect, useRef, useState } from 'react'
import Text, { TextScale } from 'particles/Text'
import Icon, { IconSize } from 'particles/Icon'
import { StyledCheckboxContainer, StyledNoSelectedContainer, StyledCheckbox } from './style'
import { useTranslation } from 'react-i18next'

export type CheckboxSize = 's' | 'm'
export type CheckboxSelectState = 'notSelected' | 'selected' | 'indeterminate'

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onClick'> {
    name?: string
    onChange?: React.ChangeEventHandler<HTMLInputElement>
    onClick?: (e: React.MouseEvent) => void
    readOnlyEmptyText?: string
    size?: CheckboxSize
    checkbox?: CheckboxSelectState
    isDisabled?: boolean
    label?: string
    labelSubText?: string
    renderLabel?: (label?: string, labelSubText?: string) => React.ReactNode
    isReadOnly?: boolean
    className?: string
    dataTestId?: string
    renderPrefix?: () => React.ReactNode
}

export const sizeMap: Record<
    CheckboxSize,
    { iconSize: IconSize; checkboxSize: string; outlineSize: string; textScale: TextScale }
> = {
    m: {
        iconSize: '20px',
        checkboxSize: '28px',
        outlineSize: '32px',
        textScale: 'bodyM400',
    },
    s: {
        iconSize: '16px',
        checkboxSize: '24px',
        outlineSize: '28px',
        textScale: 'bodyS400',
    },
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            name,
            onChange,
            onClick,
            readOnlyEmptyText,
            size = 's',
            checkbox = 'notSelected',
            isDisabled = false,
            label,
            labelSubText,
            isReadOnly = false,
            className,
            dataTestId,
            renderLabel,
            renderPrefix,
            ...props
        },
        ref,
    ) => {
        const [t] = useTranslation()
        const { iconSize, textScale } = sizeMap[size]
        const labelRef = useRef<HTMLDivElement>(null)
        // This will be use to calculate alignItems
        const [offsetHeight, setOffsetHeight] = useState(labelRef.current?.offsetHeight)

        useEffect(() => {
            setOffsetHeight(labelRef.current?.offsetHeight)
        }, [labelRef.current?.offsetHeight])

        return (
            <>
                {isReadOnly ? (
                    checkbox === 'notSelected' ? (
                        <StyledNoSelectedContainer ref={ref} size={size} className={className}>
                            <Text color="disable" scale={textScale}>
                                {readOnlyEmptyText ? readOnlyEmptyText : t('placeholderText.dropdownMenu.noItem')}
                            </Text>
                        </StyledNoSelectedContainer>
                    ) : (
                        <StyledCheckboxContainer
                            ref={ref}
                            textScale={textScale}
                            iconSize={size}
                            containerHeight={offsetHeight}
                            className={className}
                        >
                            {renderPrefix?.()}
                            <Icon
                                className="checkbox-icon"
                                name={checkbox === 'selected' ? 'check' : 'minus'}
                                color="currentColor"
                                size={iconSize}
                            />
                            {renderLabel
                                ? renderLabel(label, labelSubText)
                                : label && (
                                      <Text ref={labelRef} className="label" scale={textScale}>
                                          {label}
                                      </Text>
                                  )}
                        </StyledCheckboxContainer>
                    )
                ) : (
                    <StyledCheckboxContainer
                        ref={ref}
                        textScale={textScale}
                        iconSize={size}
                        containerHeight={offsetHeight}
                        className={className}
                    >
                        {renderPrefix?.()}
                        <div className="checkbox-container">
                            <StyledCheckbox
                                data-testid={dataTestId}
                                disableRipple
                                iconSize={size}
                                checked={checkbox === 'selected'}
                                indeterminate={checkbox === 'indeterminate'}
                                disabled={isDisabled}
                                onChange={onChange}
                                onClick={onClick}
                                inputProps={{ 'aria-label': name }}
                            />
                        </div>
                        {renderLabel
                            ? renderLabel(label, labelSubText)
                            : label && (
                                  <Text ref={labelRef} className="label" scale={textScale}>
                                      {label}
                                  </Text>
                              )}
                    </StyledCheckboxContainer>
                )}
            </>
        )
    },
)

Checkbox.displayName = 'Checkbox'

export default Checkbox
